/**
 * CompactBriefOverlay · single-paragraph brief form for ?embed=widget&compact=1
 * ───────────────────────────────────────────────────────────────────────
 * Aurelion-side ask 27 Jun 2026 · "cut time-to-Zoho-ticket from ~30s to ~5s
 * for members who've already signalled intent by clicking a card."
 *
 * Renders ONLY when the URL carries ?embed=widget&compact=1. When it does,
 * the CSS in /app/frontend/src/styles/embed.css hides the underlying Table/
 * Escape/Enjoy surface and this overlay becomes the only visible UI.
 *
 * Wire:
 *   1. URL params (member_email, member_first, member_last, member_phone,
 *      member_card, tenant) are read by initEmbedContext() at boot and
 *      persisted to sessionStorage as `lc_embed_ctx_v1`.
 *   2. Benefit (table | escape | enjoy) is inferred from the URL pathname.
 *   3. Submit posts to POST /api/lc/embed-submit — same pipe as the full
 *      surface. Server handles HMAC sign + outbound to mindescapes.club.
 *   4. Success shows the lc_ref and a "Concierge® will be in touch"
 *      message. No redirect — host page can close the iframe.
 */
import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getEmbedContext, isCompactEmbed } from "@/lib/embed-context";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ── Visual tokens (match /escape and /enjoy editorial palette) ──────────
const T = {
  paper: "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
  brassDim: "#C6A560",
};

const BENEFIT_LABEL = {
  table:  { eyebrow: "BY THE CONCIERGE® · TABLE",   headline: "Tell us about the table.",   placeholder: "e.g. 2 of us, dinner this Friday around 8pm in Bandra. Vegetarian options please." },
  escape: { eyebrow: "BY THE CONCIERGE® · ESCAPE",  headline: "Tell us about the escape.",   placeholder: "e.g. Honeymoon week in mid-July, hill station, boutique stay under ₹40k/night, romantic dining." },
  enjoy:  { eyebrow: "BY THE CONCIERGE® · ENJOY",   headline: "Tell us about the night.",    placeholder: "e.g. Two tickets to anything happening in Mumbai next Saturday night — open to live music, comedy, theatre." },
  care:   { eyebrow: "BY THE CONCIERGE® · CARE",    headline: "Who do you need to take care of?", placeholder: "e.g. ENT specialist for my 7-year-old in Lisbon, ideally tomorrow morning — Portuguese or English speaking." },
};

function inferBenefit(pathname) {
  if (!pathname) return "table";
  if (pathname.startsWith("/escape")) return "escape";
  if (pathname.startsWith("/enjoy"))  return "enjoy";
  if (pathname.startsWith("/care"))   return "care";
  return "table";
}

export default function CompactBriefOverlay() {
  const location = useLocation();
  const active = isCompactEmbed();
  const ctx = useMemo(() => getEmbedContext() || {}, []);
  const benefit = useMemo(() => inferBenefit(location.pathname), [location.pathname]);
  const cfg = BENEFIT_LABEL[benefit];

  const [name, setName] = useState(
    [ctx.member_first, ctx.member_last].filter(Boolean).join(" ").trim()
  );
  const [email, setEmail]   = useState(ctx.member_email || "");
  const [phone, setPhone]   = useState(ctx.member_phone || "");
  const [brief, setBrief]   = useState("");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  const [result, setResult] = useState(null);

  if (!active) return null;

  async function submit(e) {
    e.preventDefault();
    if (!brief.trim()) {
      setError("Just a line or two about what you're looking for.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("We'll need your email to write back.");
      return;
    }
    setBusy(true);
    setError(null);
    const [first, ...rest] = (name || "").trim().split(/\s+/);
    try {
      const r = await fetch(`${API}/api/lc/embed-submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant: ctx.tenant || "aurelion",
          benefit,
          member: {
            first_name: first || "",
            last_name:  rest.join(" "),
            email:      email.trim(),
            whatsapp:   phone.trim() || undefined,
            member_card: ctx.member_card || undefined,
          },
          request: { brief: brief.trim() },
          source: "lesconcierges-embed-compact",
          page_url: window.location.href,
          session_id: ctx.session_id || undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `submit_failed (${r.status})`);
      setResult(data);
    } catch (err) {
      setError(err.message || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  // ── Success state ──────────────────────────────────────────────────
  if (result) {
    return (
      <Overlay testid="lc-compact-overlay-success">
        <div style={S.eyebrow}>{cfg.eyebrow}</div>
        <h1 style={S.successHeadline} data-testid="lc-compact-success-headline">
          Your brief is with the Concierge®.
        </h1>
        <p style={S.successBody}>
          We&apos;ll be in touch on <strong>{email}</strong> very shortly with the next step.
        </p>
        <div data-testid="lc-compact-success-ref" style={S.refChip}>
          {result.lc_ref || "REF-PENDING"}
        </div>
      </Overlay>
    );
  }

  // ── Form state ─────────────────────────────────────────────────────
  return (
    <Overlay testid="lc-compact-overlay">
      <div style={S.eyebrow} data-testid="lc-compact-eyebrow">{cfg.eyebrow}</div>
      <h1 style={S.headline} data-testid="lc-compact-headline">{cfg.headline}</h1>
      <p style={S.lede}>One line. We&apos;ll handle the rest.</p>

      <form onSubmit={submit} style={S.form}>
        <textarea
          data-testid="lc-compact-brief"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder={cfg.placeholder}
          rows={4}
          style={S.textarea}
          autoFocus
        />

        <div style={S.row}>
          <input
            data-testid="lc-compact-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            style={S.input}
          />
          <input
            data-testid="lc-compact-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            style={S.input}
            required
          />
        </div>

        <input
          data-testid="lc-compact-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="WhatsApp (optional, faster reply)"
          style={S.input}
        />

        {error && (
          <p style={S.error} data-testid="lc-compact-error">{error}</p>
        )}

        <button
          type="submit"
          data-testid="lc-compact-submit"
          disabled={busy}
          style={{ ...S.submit, opacity: busy ? 0.6 : 1, cursor: busy ? "wait" : "pointer" }}
        >
          {busy ? "Sending…" : "Send to the Concierge® →"}
        </button>
      </form>

      <p style={S.foot}>
        Reply on email within 4 hours, weekdays · the Concierge® is on duty.
      </p>
    </Overlay>
  );
}

// ── Layout primitive ─────────────────────────────────────────────────
function Overlay({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="lc-compact-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: T.paper,
        zIndex: 9999,
        overflowY: "auto",
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        color: T.ink,
      }}
    >
      <div
        style={{
          maxWidth: 520,
          margin: "0 auto",
          padding: "44px 28px 32px",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────
const S = {
  eyebrow: {
    fontFamily: "ui-monospace, monospace",
    fontSize: 10.5,
    letterSpacing: "0.24em",
    color: T.brass,
    fontWeight: 700,
    marginBottom: 12,
  },
  headline: {
    fontFamily: "'Fraunces', 'EB Garamond', serif",
    fontSize: "clamp(26px, 4.4vw, 34px)",
    lineHeight: 1.12,
    margin: 0,
    fontWeight: 500,
    color: T.ink,
  },
  successHeadline: {
    fontFamily: "'Fraunces', 'EB Garamond', serif",
    fontSize: "clamp(24px, 4vw, 30px)",
    lineHeight: 1.15,
    margin: 0,
    fontWeight: 500,
    color: T.ink,
  },
  successBody: {
    fontSize: 15,
    color: T.ink2,
    margin: "16px 0 22px",
    lineHeight: 1.55,
  },
  refChip: {
    display: "inline-block",
    padding: "9px 16px",
    background: T.card,
    border: `1px solid ${T.brassDim}`,
    borderRadius: 999,
    fontFamily: "ui-monospace, monospace",
    fontSize: 12.5,
    color: T.brass,
    fontWeight: 600,
    letterSpacing: "0.06em",
  },
  lede: {
    fontSize: 14.5,
    color: T.ink2,
    margin: "10px 0 24px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  textarea: {
    width: "100%",
    minHeight: 110,
    resize: "vertical",
    padding: "14px 16px",
    border: `1px solid ${T.line2}`,
    borderRadius: 14,
    background: T.card,
    color: T.ink,
    fontSize: 15.5,
    lineHeight: 1.5,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },
  input: {
    padding: "12px 14px",
    border: `1px solid ${T.line2}`,
    borderRadius: 12,
    background: T.card,
    color: T.ink,
    fontSize: 14.5,
    fontFamily: "inherit",
    outline: "none",
    minWidth: 0,
    boxSizing: "border-box",
  },
  submit: {
    marginTop: 4,
    padding: "14px 22px",
    background: T.ink,
    color: T.card,
    border: 0,
    borderRadius: 14,
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: "0.01em",
    fontFamily: "inherit",
  },
  error: {
    fontSize: 13,
    color: "#9a3535",
    margin: "4px 0 0",
  },
  foot: {
    fontFamily: "ui-monospace, monospace",
    fontSize: 11,
    letterSpacing: "0.06em",
    color: T.ink3,
    marginTop: 28,
    textAlign: "center",
  },
};
